import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import type {
  ClickExpandBlock,
  ClickExpandImageSize,
  ClickExpandTitleLevel,
  ClickExpandTopic,
} from '@course-authoring/shared-types';
import {
  isRichTextContentEmpty,
  useTheme,
} from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import type {
  ClickExpandNarrationState,
  ClickExpandNarrationTarget,
  ClickExpandNarrationTargetChange,
} from './narrationEvents';
import { COURSE_SCROLL_ROOT_SELECTOR } from './narrationManualIntent';
import { NARRATION_SCROLL_START_EVENT } from './narrationScroll';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { createContentBlockStyles } from './styles/contentBlocks';
import type { ContentBlockStyles } from './styles/contentBlocks';
import { getTitleTypography } from './styles/titleTypography';
import {
  getTextImageSplitGridStyle,
  textImageSplitMobileImageStyle,
} from './styles/textImageSplit';
import { CheckmarkIcon, ChevronDownIcon } from './contentBlockIcons';
import { useCollapsiblePanel } from './hooks/useCollapsiblePanel';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { useVisitedItems } from './hooks/useVisitedItems';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateRichTextHTML,
  type RichTextRendererExtensions,
} from './richTextRenderer';
import { InteractionMessage } from './InteractionMessage';
import { getStandardInteractionWidth } from './interactionLayout';
import { useCardBackground } from './SectionSurfaceContext';
import { RichTextContent } from './RichTextContent';

export interface ClickExpandRendererProps {
  block: ClickExpandBlock;
  onInteractionComplete?: () => void;
  /** The topic index is zero-based; narration seam names remain one-based (`topic:N`). */
  onTopicSelected?: (topicId: string, topicIndex: number) => void;
  onTopicClosed?: (topicId: string, topicIndex: number) => void;
  /**
   * Narration hook. Called with the mounted target and `null` as the previous
   * target, then with `null` and that same target on unmount, so the owner can
   * clear only its own entry. Omit where no narration is listening.
   */
  onNarrationTargetChange?: ClickExpandNarrationTargetChange;
}

/** Latest open of the renderer, read by the Accordion reveal when its timer fires. */
interface LatestTopicOpen {
  topicId: string;
  source: 'learner' | 'narration';
  /** Set by `cancelPendingReveal`; a cancelled narration reveal never scrolls. */
  revealCancelled: boolean;
}

function findTopicIndex(topics: readonly ClickExpandTopic[], topicId: string): number {
  return topics.findIndex((topic) => topic.id === topicId);
}

const contentResetStyles = createRichTextContentResetStyles(
  'click-expand-content',
  '0.5em',
  ['.click-expand-panel']
);

const ACCORDION_AUTO_SCROLL_BOTTOM_BUFFER_PX = 96;
const ACCORDION_AUTO_SCROLL_DELAY_MS = 275;
const ACCORDION_AUTO_SCROLL_THRESHOLD_PX = 4;
const SCROLLABLE_OVERFLOW_VALUES = new Set(['auto', 'scroll', 'overlay']);

function isScrollableOverflowValue(overflowValue: string): boolean {
  return SCROLLABLE_OVERFLOW_VALUES.has(overflowValue);
}

function getNearestScrollContainer(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const computedStyle = window.getComputedStyle(current);
    if (
      isScrollableOverflowValue(computedStyle.overflowY) ||
      isScrollableOverflowValue(computedStyle.overflow)
    ) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

function getResponsiveClickExpandContainerStyle(
  baseStyle: React.CSSProperties,
  width: string
): React.CSSProperties {
  return {
    ...baseStyle,
    width,
    maxWidth: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  };
}

function getResponsiveTopicLayoutStyle(
  baseStyle: React.CSSProperties,
  hasImage: boolean,
  isMobile: boolean,
  imageSize: ClickExpandImageSize | undefined
): React.CSSProperties {
  if (isMobile) {
    return {
      ...baseStyle,
      flexDirection: 'column',
      alignItems: 'stretch',
    };
  }

  if (!hasImage) {
    return baseStyle;
  }

  return {
    ...baseStyle,
    ...getTextImageSplitGridStyle(imageSize),
  };
}

function getResponsiveTopicTextStyle(
  baseStyle: React.CSSProperties
): React.CSSProperties {
  return {
    ...baseStyle,
    minWidth: 0,
    width: '100%',
  };
}

function getResponsiveTopicImageStyle(
  baseStyle: React.CSSProperties,
  isMobile: boolean
): React.CSSProperties {
  if (isMobile) {
    return {
      ...baseStyle,
      ...textImageSplitMobileImageStyle,
      maxWidth: '100%',
    };
  }

  return {
    ...baseStyle,
    width: '100%',
    maxWidth: '100%',
  };
}

// --- Accordion Layout ---

interface AccordionItemProps {
  topic: ClickExpandTopic;
  isExpanded: boolean;
  isViewed: boolean;
  isLastItem: boolean;
  requireInteraction: boolean;
  onToggle: () => void;
  isMobile: boolean;
  imageSize: ClickExpandImageSize | undefined;
  styles: ContentBlockStyles;
  extensions: RichTextRendererExtensions;
  latestOpenRef: React.RefObject<LatestTopicOpen | null>;
}

function AccordionItem({
  topic,
  isExpanded,
  isViewed,
  isLastItem,
  requireInteraction,
  onToggle,
  isMobile,
  imageSize,
  styles,
  extensions,
  latestOpenRef,
}: AccordionItemProps): React.ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const lastAutoScrollHeightRef = useRef<number>(0);
  const [isHovered, setIsHovered] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const contentHtml = useMemo(
    () => safeGenerateRichTextHTML(topic.content, extensions),
    [topic.content, extensions]
  );

  const triggerId = `click-expand-trigger-${topic.id}`;
  const panelId = `click-expand-panel-${topic.id}`;

  const { contentRef, contentHeight, panelStyle } = useCollapsiblePanel({
    isExpanded,
    prefersReducedMotion,
    baseStyle: styles.clickExpandAccordionPanel,
    measureKey: `${contentHtml}|${topic.imageUrl ?? ''}|${isMobile ? 'mobile' : 'desktop'}`,
    minExpandedHeight: 200,
    observeResize: true,
    remeasureOnExpandFrame: true,
  });

  const scrollExpandedPanelIntoView = useCallback((isNarrationReveal: boolean) => {
    const panelElement = panelRef.current;
    if (!panelElement || typeof window === 'undefined') return;

    // Sent only when a scroll follows. It is the signal narration's own scrolls send,
    // so the scroll is not read as learner scroll intent.
    const announceNarrationScroll = (): void => {
      if (isNarrationReveal) {
        panelElement.closest(COURSE_SCROLL_ROOT_SELECTOR)?.dispatchEvent(new Event(NARRATION_SCROLL_START_EVENT));
      }
    };

    const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';
    const scrollContainer = getNearestScrollContainer(panelElement);

    if (
      scrollContainer &&
      scrollContainer !== document.body &&
      scrollContainer !== document.documentElement
    ) {
      const panelRect = panelElement.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const desiredBottom = containerRect.bottom - ACCORDION_AUTO_SCROLL_BOTTOM_BUFFER_PX;
      const overflow = panelRect.bottom - desiredBottom;

      if (overflow <= ACCORDION_AUTO_SCROLL_THRESHOLD_PX) {
        return;
      }

      announceNarrationScroll();
      if (typeof scrollContainer.scrollBy === 'function') {
        scrollContainer.scrollBy({
          top: overflow,
          behavior,
        });
      } else {
        scrollContainer.scrollTop += overflow;
      }
      return;
    }

    if (typeof window.scrollBy !== 'function') return;

    const panelRect = panelElement.getBoundingClientRect();
    const desiredBottom = window.innerHeight - ACCORDION_AUTO_SCROLL_BOTTOM_BUFFER_PX;
    const overflow = panelRect.bottom - desiredBottom;

    if (overflow <= ACCORDION_AUTO_SCROLL_THRESHOLD_PX) {
      return;
    }

    announceNarrationScroll();
    window.scrollBy({
      top: overflow,
      behavior,
    });
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!isExpanded) {
      lastAutoScrollHeightRef.current = 0;
      return;
    }

    if (contentHeight <= 0 || contentHeight <= lastAutoScrollHeightRef.current) {
      return;
    }

    lastAutoScrollHeightRef.current = contentHeight;
    const timeoutId = window.setTimeout(() => {
      // Read at fire time so a narration cancel that lands after scheduling still stops the scroll.
      const latestOpen = latestOpenRef.current;
      const isNarrationReveal = latestOpen?.topicId === topic.id && latestOpen.source === 'narration';
      if (isNarrationReveal && latestOpen.revealCancelled) return;
      scrollExpandedPanelIntoView(isNarrationReveal);
    }, ACCORDION_AUTO_SCROLL_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [contentHeight, isExpanded, latestOpenRef, scrollExpandedPanelIntoView, topic.id]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  const hasImage = Boolean(topic.imageUrl);
  const panelContentStyle = getResponsiveTopicLayoutStyle(
    styles.clickExpandAccordionPanelContent,
    hasImage,
    isMobile,
    imageSize
  );
  const contentTextStyle = getResponsiveTopicTextStyle(styles.clickExpandTabContentText);
  const imageStyle = getResponsiveTopicImageStyle(
    styles.clickExpandAccordionImage,
    isMobile
  );

  const getTriggerStyle = (): React.CSSProperties => {
    const base = styles.clickExpandAccordionTrigger;
    if (isExpanded) {
      return { ...base, ...styles.clickExpandAccordionTriggerActive };
    }
    if (isHovered) {
      return { ...base, ...styles.clickExpandAccordionTriggerHover };
    }
    return base;
  };

  const getItemStyle = (): React.CSSProperties => {
    const base = isExpanded
      ? { ...styles.clickExpandAccordionItem, ...styles.clickExpandAccordionItemExpanded }
      : styles.clickExpandAccordionItem;

    if (isLastItem) {
      return {
        ...base,
        borderBottom: 0,
        paddingBottom: 0,
      };
    }

    return base;
  };

  const renderIcon = () => {
    if (requireInteraction && isViewed && !isExpanded) {
      return <CheckmarkIcon style={{ ...styles.clickExpandCheckmark, color: 'inherit' }} />;
    }
    return (
      <span style={styles.clickExpandAccordionIcon} aria-hidden="true">
        {isExpanded ? '\u2013' : '+'}
      </span>
    );
  };

  return (
    <div style={getItemStyle()}>
      <button
        id={triggerId}
        data-narration-topic-control="true"
        style={getTriggerStyle()}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        type="button"
      >
        <span style={styles.clickExpandAccordionTopicTitle}>{topic.title}</span>
        {renderIcon()}
      </button>

      <div
        ref={panelRef}
        id={panelId}
        className="click-expand-panel"
        style={panelStyle}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!isExpanded}
      >
        <div ref={contentRef}>
          <div style={panelContentStyle}>
            {isMobile && topic.imageUrl && (
              <img
                src={topic.imageUrl}
                alt={topic.imageAlt || ''}
                style={imageStyle}
              />
            )}
            <RichTextContent
              className="click-expand-content"
              style={contentTextStyle}
              html={contentHtml}
            />
            {!isMobile && topic.imageUrl && (
              <img
                src={topic.imageUrl}
                alt={topic.imageAlt || ''}
                style={imageStyle}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface AccordionLayoutProps {
  topics: ClickExpandTopic[];
  viewedTopics: Set<string>;
  requireInteraction: boolean;
  expandedTopicId: string | null;
  onToggleTopic: (topicId: string) => void;
  latestOpenRef: React.RefObject<LatestTopicOpen | null>;
  isMobile: boolean;
  imageSize: ClickExpandImageSize | undefined;
  styles: ContentBlockStyles;
  extensions: RichTextRendererExtensions;
}

function AccordionLayout({
  topics,
  viewedTopics,
  requireInteraction,
  expandedTopicId,
  onToggleTopic,
  latestOpenRef,
  isMobile,
  imageSize,
  styles,
  extensions,
}: AccordionLayoutProps): React.ReactElement {
  const cardBackground = useCardBackground();

  return (
    <div style={{ ...styles.clickExpandAccordionList, backgroundColor: cardBackground }}>
      {topics.map((topic) => (
        <AccordionItem
          key={topic.id}
          topic={topic}
          isExpanded={expandedTopicId === topic.id}
          isViewed={viewedTopics.has(topic.id)}
          isLastItem={topic.id === topics[topics.length - 1]?.id}
          requireInteraction={requireInteraction}
          onToggle={() => onToggleTopic(topic.id)}
          isMobile={isMobile}
          imageSize={imageSize}
          styles={styles}
          extensions={extensions}
          latestOpenRef={latestOpenRef}
        />
      ))}
    </div>
  );
}

// --- Top Tabs Layout ---

interface TopTabsLayoutProps {
  topics: ClickExpandTopic[];
  activeIndex: number;
  onActivateTopic: (index: number) => void;
  onVisitTopic: (id: string) => void;
  isMobile: boolean;
  imageSize: ClickExpandImageSize | undefined;
  styles: ContentBlockStyles;
  extensions: RichTextRendererExtensions;
}

function TopTabsLayout({
  topics,
  activeIndex,
  onActivateTopic,
  onVisitTopic,
  isMobile,
  imageSize,
  styles,
  extensions,
}: TopTabsLayoutProps): React.ReactElement {
  const { t } = useTranslation();
  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);
  const { tokens } = useTheme();
  const cardBackground = useCardBackground();

  useEffect(() => {
    const firstTopic = topics[0];
    if (firstTopic) {
      onVisitTopic(firstTopic.id);
    }
    // Only on mount

  }, []);

  const handleTabClick = (index: number) => {
    onActivateTopic(index);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextIndex = Number(e.target.value);
    if (Number.isNaN(nextIndex)) return;
    onActivateTopic(nextIndex);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let newIndex = index;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      newIndex = (index + 1) % topics.length;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      newIndex = (index - 1 + topics.length) % topics.length;
    }
    if (newIndex !== index && topics[newIndex]) {
      onActivateTopic(newIndex);
    }
  };

  const activeTopic = topics[activeIndex];
  const activeContent = activeTopic?.content ?? { type: 'doc' as const, content: [] };
  const contentHtml = useMemo(
    () => safeGenerateRichTextHTML(activeContent, extensions),
    [activeContent, extensions]
  );
  const hasImage = Boolean(activeTopic?.imageUrl);

  const tabContentStyle = getResponsiveTopicLayoutStyle(
    { ...styles.clickExpandTabContent, backgroundColor: cardBackground },
    hasImage,
    isMobile,
    imageSize
  );
  const tabTextStyle = getResponsiveTopicTextStyle(
    styles.clickExpandTabContentText
  );
  const tabImageStyle = getResponsiveTopicImageStyle(
    styles.clickExpandTabContentImage,
    isMobile
  );
  const mobileSelectStyle = useMemo<React.CSSProperties>(() => ({
    ...styles.clickExpandTabSelect,
    fontSize: tokens.typography.mobile.body.size,
    lineHeight: tokens.typography.mobile.body.lineHeight,
    fontWeight: tokens.typography.mobile.h3.weight,
    boxShadow: 'none',
  }), [styles, tokens]);
  const mobileShellStyle = useMemo<React.CSSProperties>(() => ({
    // NA-205: 16px felt abruptly tight vs the roomy desktop tab content; 24px reads better.
    padding: tokens.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.lg,
  }), [tokens]);
  const mobileTabContentStyle = useMemo<React.CSSProperties>(() => ({
    ...tabContentStyle,
    padding: 0,
  }), [tabContentStyle]);
  const getDesktopTabStyle = useCallback((index: number, isActive: boolean): React.CSSProperties => {
    const isHovered = hoveredTabIndex === index && !isActive;
    return {
      ...styles.clickExpandTab,
      ...(isHovered ? styles.clickExpandTabHover : {}),
      ...(isActive ? { ...styles.clickExpandTabActive, backgroundColor: cardBackground } : {}),
      ...(index === topics.length - 1 ? { borderRight: 0 } : {}),
    };
  }, [hoveredTabIndex, styles, topics.length, cardBackground]);

  const tabShellStyle = useMemo<React.CSSProperties>(() => ({
    ...styles.clickExpandTabShell,
    backgroundColor: cardBackground,
  }), [styles, cardBackground]);

  // Early return must come after all hooks so the hook count stays stable
  // when topics empty out or activeIndex outruns a shrunk list during editing.
  if (!activeTopic) return <div />;

  if (isMobile) {
    return (
      <div style={tabShellStyle}>
        <div style={mobileShellStyle}>
          <div style={{ ...styles.clickExpandTabSelectWrapper, marginBottom: 0 }}>
            <select
              aria-label={t('click_expand.select_topic')}
              data-testid="click-expand-mobile-select"
              data-narration-topic-control="true"
              style={mobileSelectStyle}
              value={String(activeIndex)}
              onChange={handleSelectChange}
            >
              {topics.map((topic, index) => (
                <option key={topic.id} value={String(index)}>
                  {topic.title}
                </option>
              ))}
            </select>
            <span style={styles.clickExpandTabSelectIcon}>
              <ChevronDownIcon />
            </span>
          </div>

          <div
            id={`click-expand-tabpanel-${activeTopic.id}`}
            role="tabpanel"
            aria-label={activeTopic.title}
            style={mobileTabContentStyle}
          >
            {activeTopic.imageUrl && (
              <img
                src={activeTopic.imageUrl}
                alt={activeTopic.imageAlt || ''}
                style={tabImageStyle}
              />
            )}
            <RichTextContent
              className="click-expand-content"
              style={tabTextStyle}
              html={contentHtml}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={tabShellStyle}>
      <div style={styles.clickExpandTabBar} role="tablist">
        {topics.map((topic, i) => {
          const isActive = i === activeIndex;
          return (
            <button
              key={topic.id}
              role="tab"
              type="button"
              data-narration-topic-control="true"
              aria-selected={isActive}
              aria-controls={`click-expand-tabpanel-${topic.id}`}
              id={`click-expand-tab-${topic.id}`}
              tabIndex={isActive ? 0 : -1}
              style={getDesktopTabStyle(i, isActive)}
              onClick={() => handleTabClick(i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onMouseEnter={() => setHoveredTabIndex(i)}
              onMouseLeave={() => setHoveredTabIndex((prev) => (prev === i ? null : prev))}
            >
              {topic.title}
            </button>
          );
        })}
      </div>

      <div
        id={`click-expand-tabpanel-${activeTopic.id}`}
        role="tabpanel"
        aria-labelledby={`click-expand-tab-${activeTopic.id}`}
        style={tabContentStyle}
      >
        <RichTextContent
          className="click-expand-content"
          style={tabTextStyle}
          html={contentHtml}
        />
        {activeTopic.imageUrl && (
          <img
            src={activeTopic.imageUrl}
            alt={activeTopic.imageAlt || ''}
            style={tabImageStyle}
          />
        )}
      </div>
    </div>
  );
}

// --- Main Renderer ---

export function ClickExpandRenderer({
  block,
  onInteractionComplete,
  onTopicSelected,
  onTopicClosed,
  onNarrationTargetChange,
}: ClickExpandRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const {
    visitedIds: viewedTopics,
    isComplete: isInteractionComplete,
    markVisited: markViewed,
  } = useVisitedItems<string>({
    expectedCount: block.topics.length,
    onAllVisited: onInteractionComplete,
  });
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  // Refs mirror the React state above and the visited set. Narration reads them
  // through the target, so each open/close path must update them in the same call.
  const topicsRef = useRef(block.topics);
  topicsRef.current = block.topics;
  const openTopicIdRef = useRef<string | null>(
    block.expandType === 'accordion' ? null : (block.topics[0]?.id ?? null)
  );
  // Tabs show topic 0 on mount; count it before the mount effect runs so an early getState() sees it.
  const openedTopicIdsRef = useRef(new Set<string>(
    block.expandType === 'accordion' || !block.topics[0] ? [] : [block.topics[0].id]
  ));
  const latestOpenRef = useRef<LatestTopicOpen | null>(null);
  const expandTypeRef = useRef(block.expandType);
  expandTypeRef.current = block.expandType;
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const baseStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  // NA-205: on narrow phones (≤576px) tighten the accordion card padding/gap and drop the
  // panel body copy to 16px. (Width is already 90% via getStandardInteractionWidth at ≤768.)
  // clickExpandTabContentText is shared with the tabs layout, so this also covers tabs' body copy.
  const styles = useMemo(() => {
    if (!isSmallMobile) return baseStyles;
    return {
      ...baseStyles,
      clickExpandAccordionList: {
        ...baseStyles.clickExpandAccordionList,
        padding: '24px 26px 24px',
        gap: '24px',
      },
      clickExpandAccordionItem: {
        ...baseStyles.clickExpandAccordionItem,
        paddingBottom: '24px',
      },
      clickExpandAccordionTopicTitle: {
        ...baseStyles.clickExpandAccordionTopicTitle,
        fontSize: tokens.typography.sizes.ui, // NA-205: 18px row header ≤576px (was 20px body)
      },
      clickExpandTabContentText: {
        ...baseStyles.clickExpandTabContentText,
        fontSize: tokens.typography.mobile.body.size,
        lineHeight: tokens.typography.mobile.body.lineHeight,
      },
    };
  }, [baseStyles, isSmallMobile, tokens]);
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const requireInteraction = block.requireInteraction ?? false;
  const clickExpandWidth = getStandardInteractionWidth(tokens, isMobile);
  const containerStyle = useMemo(
    () => getResponsiveClickExpandContainerStyle(styles.clickExpandContainer, clickExpandWidth),
    [clickExpandWidth, styles]
  );
  const titleLevel: ClickExpandTitleLevel = block.titleLevel ?? 'h2';
  const titleTypography = useMemo(
    () => getTitleTypography(tokens, titleLevel, { isMobile }),
    [isMobile, titleLevel, tokens],
  );
  const titleStyle = useMemo<React.CSSProperties>(() => ({
    ...styles.clickExpandTitle,
    fontSize: titleTypography.size,
    fontWeight: titleTypography.weight,
    lineHeight: titleTypography.lineHeight,
  }), [styles.clickExpandTitle, titleTypography]);

  const descriptionHtml = useMemo(
    () => safeGenerateRichTextHTML(block.description, extensions),
    [block.description, extensions]
  );
  const showDescription = !isRichTextContentEmpty(block.description);
  const markTopicVisited = useCallback((topicId: string) => {
    openedTopicIdsRef.current.add(topicId);
    markViewed(topicId);
  }, [markViewed]);

  /** Every open path records the open topic, its source and its visit here, in the same call. */
  const recordTopicOpen = useCallback((topicId: string, source: LatestTopicOpen['source']) => {
    openTopicIdRef.current = topicId;
    latestOpenRef.current = { topicId, source, revealCancelled: false };
    markTopicVisited(topicId);
  }, [markTopicVisited]);

  const handleAccordionToggle = (topicId: string) => {
    const isClosing = openTopicIdRef.current === topicId;
    setExpandedTopicId(isClosing ? null : topicId);

    const topicIndex = findTopicIndex(block.topics, topicId);
    if (isClosing) {
      openTopicIdRef.current = null;
      latestOpenRef.current = null;
      onTopicClosed?.(topicId, topicIndex);
    } else {
      recordTopicOpen(topicId, 'learner');
      onTopicSelected?.(topicId, topicIndex);
    }
  };

  const handleTabActivate = (index: number) => {
    const topic = block.topics[index];
    if (!topic) return;
    setActiveTabIndex(index);
    recordTopicOpen(topic.id, 'learner');
    onTopicSelected?.(topic.id, index);
  };

  // Created once per mounted renderer. Each function reads refs, the stable useState
  // setters, or recordTopicOpen (built on useVisitedItems' stable markVisited), so the
  // first-render closures stay current.
  const [narrationTarget] = useState<ClickExpandNarrationTarget>(() => ({
    getState: (): ClickExpandNarrationState => {
      const topics = topicsRef.current;
      const openTopicId = openTopicIdRef.current;
      const openTopicIndex = openTopicId === null ? -1 : findTopicIndex(topics, openTopicId);
      const openedTopicIndices = new Set<number>();
      openedTopicIdsRef.current.forEach((topicId) => {
        const index = findTopicIndex(topics, topicId);
        if (index >= 0) openedTopicIndices.add(index);
      });
      return {
        openTopicIndex: openTopicIndex >= 0 ? openTopicIndex : null,
        openedTopicIndices,
      };
    },
    openTopic: (index: number): boolean => {
      const topic = topicsRef.current[index];
      if (!topic) return false;
      recordTopicOpen(topic.id, 'narration');
      if (expandTypeRef.current === 'accordion') {
        setExpandedTopicId(topic.id);
      } else {
        setActiveTabIndex(index);
      }
      return true;
    },
    cancelPendingReveal: (): void => {
      const latestOpen = latestOpenRef.current;
      if (latestOpen?.source === 'narration') latestOpen.revealCancelled = true;
    },
  }));

  const narrationTargetChangeRef = useRef(onNarrationTargetChange);
  narrationTargetChangeRef.current = onNarrationTargetChange;
  useLayoutEffect(() => {
    narrationTargetChangeRef.current?.(narrationTarget, null);
    return () => {
      narrationTargetChangeRef.current?.(null, narrationTarget);
    };
  }, [narrationTarget]);

  return (
    <div style={containerStyle} data-testid="click-expand-container">
      <style>{contentResetStyles}</style>

      <div style={styles.clickExpandHeader}>
        {block.title && (
          React.createElement(titleLevel, { style: titleStyle }, block.title)
        )}
        {showDescription && (
          <RichTextContent
            className="click-expand-content"
            style={styles.clickExpandDescription}
            html={descriptionHtml}
          />
        )}
      </div>

      {block.expandType === 'accordion' ? (
        <AccordionLayout
          topics={block.topics}
          viewedTopics={viewedTopics}
          requireInteraction={requireInteraction}
          expandedTopicId={expandedTopicId}
          onToggleTopic={handleAccordionToggle}
          latestOpenRef={latestOpenRef}
          isMobile={isMobile}
          imageSize={block.imageSize}
          styles={styles}
          extensions={extensions}
        />
      ) : (
        <TopTabsLayout
          topics={block.topics}
          activeIndex={activeTabIndex}
          onActivateTopic={handleTabActivate}
          onVisitTopic={markTopicVisited}
          isMobile={isMobile}
          imageSize={block.imageSize}
          styles={styles}
          extensions={extensions}
        />
      )}

      <InteractionMessage
        variant="boxed"
        requireInteraction={requireInteraction}
        isComplete={isInteractionComplete}
        message={block.interactionMessage}
        fallbackMessage={t('click_expand.view_all_topics')}
      />
    </div>
  );
}

ClickExpandRenderer.displayName = 'ClickExpandRenderer';
